// Components
import BadgeDot from "components/Atoms/BadgeDot";
import Box from "components/Atoms/Box";

// Utils
import { DateTime } from "luxon";
import i18n from "i18n";
import { getSubset } from "utils/general";

export const LINE_COLORS = [
  "info",
  "warning",
  "primary",
  "secondary",
  "success",
  "dark",
  "error",
  "light",
];

export const createChartXAxisLabel = (datetime, granularity = null) => {
  // Localized month names ("jan", "fev"…) so the month/year read clearly.
  const date = DateTime.fromISO(datetime, { setZone: true }).setLocale(i18n.language || "pt");
  switch (granularity) {
    case "MIN15":
      return date.toFormat("dd LLL HH:mm");
    case "HOUR":
      return date.toFormat("dd LLL yyyy HH:00");
    case "DAY":
      return date.toFormat("dd LLL yyyy");
    case "MONTH":
      return date.toFormat("LLL yyyy");
    case "YEAR":
      return date.toFormat("yyyy");
    default:
      return date.toFormat("dd LLL yyyy");
  }
};

/**
 * Utility function to generate line chart data from given metrics.
 *
 * @param {Array<{ label: string, data: Array<{ datetime_timestamp: string, [field]: float> }, field: string }>} metrics
 * @param {string} granularity
 * @returns {Object} - The generated line chart parameters.
 */

export const generateLineChartData = (metrics, granularity, color) => {
  const firstNonEmpty = metrics.find((m) => m.data?.length > 0);
  return {
    labels: firstNonEmpty
      ? firstNonEmpty.data.map((metric) =>
          createChartXAxisLabel(metric.datetime_timestamp, granularity)
        )
      : [createChartXAxisLabel(DateTime.local())],
    datasets: metrics.map((metric, idx) => ({
      label: metric.label,
      color: color ?? LINE_COLORS[idx],
      data:
        metric.data?.length > 0 && !!metric.data[0]
          ? metric.data.map((dataPoint) => dataPoint[metric.field])
          : ["0"],
    })),
  };
};

/**
 * The same as above but with description
 */
export const generateLineChart = (metrics, granularity, color) => {
  const generatedChartData = generateLineChartData(metrics, granularity, color);

  return {
    description: (
      <Box display="flex" ml={-1}>
        {metrics.map((metric, idx) => (
          <BadgeDot
            key={metric.label}
            color={color ?? LINE_COLORS[idx]}
            badgeContent={metric.label}
          />
        ))}
      </Box>
    ),
    chart: generatedChartData,
  };
};

export const generateBarChart = generateLineChart;

/**
 * Utility function to generate doughnut chart data from given metrics.
 *
 * @param {labels: Array<string>, id_field: string, value_field: string,
 *         data: Array<{ [id_field]: string, [value_field]: float> }, idToName: Function }>} metrics
 * @returns {Object} - The generated doughnut chart parameters.
 */
export const generateDoughtnutChartData = (label, idField, valueField, data, idToName) => {
  const sum = data?.reduce((acc, dataPoint) => acc + Math.abs(dataPoint[valueField]), 0) || 1;

  return {
    labels:
      !!data && data.length > 0
        ? data.map((dataPoint) => idToName(dataPoint[idField]))
        : ["No Data"],
    datasets:
      !!data && data.length > 0
        ? {
            label,
            backgroundColors: getSubset(LINE_COLORS, data.length),
            data: data.map((dataPoint) =>
              ((Math.abs(dataPoint[valueField]) / sum) * 100).toFixed(2)
            ),
          }
        : {
            label,
            backgroundColors: getSubset(LINE_COLORS, 1),
            data: ["100"],
          },
    ids: !!data && data.length > 0 ? data.map((dataPoint) => dataPoint[idField]) : ["No ID"],
  };
};

/**
 * Shorten a meter identifier for chart legend display.
 * Portuguese EAN meter points (PT + 16–20 alphanumeric chars) are truncated to last 6 chars.
 */
export const shortMeterLabel = (raw) => {
  if (!raw) return raw;
  if (/^PT[A-Z0-9]{16,20}$/i.test(raw)) return `…${raw.slice(-6)}`;
  return raw;
};

export default {
  generateLineChartData,
  LINE_COLORS,
  createChartXAxisLabel,
  generateDoughtnutChartData,
  generateLineChart,
  shortMeterLabel,
};
